import { useState } from "react";
import { z } from "zod";
import { Button } from "components/ui/button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCircleDot } from "@fortawesome/pro-solid-svg-icons";
import { faCircle } from "@fortawesome/pro-regular-svg-icons";
import Metadata from "components/metadata/Metadata";
import {
  NextStepActionResponse,
  VisibleStepMetadata,
  StepDisclaimer,
  InputResponse,
  useSetApplicationStepAnswerMutation,
  useUpdateStepAnswerCyberSecurityAuditLogMutation,
} from "pages/application/applicationService";
import { Input } from "components/ui/input/Input";
import PhoneNumberInput from "components/ui/PhoneNumberInput/PhoneNumberInput";
import { Select } from "components/ui/select/Select";
import { useSelector } from "react-redux";
import { getCurrentUserApplicationId } from "features/authSlice";
import DisclaimerText from "components/ui/disclaimer-text/DisclaimerText";
import { SkipButton } from "pages/application/question/Question";
import { QuestionActions } from "pages/application/templates/shared/QuestionActions";
import { QuestionNavigation } from "pages/application/templates/shared/QuestionNavigation";
import { trackFSStepCompleted } from "utilities/full-story";
import OtherButtonAndText from "pages/application/templates/SingleChoice/OtherButtonAndText";

export interface QuestionnaireChoiceStepProps {
  stepActions: NextStepActionResponse[];
  stepCode: string;
  stepLabel: string;
  metadata?: VisibleStepMetadata[];
  skipAction?: NextStepActionResponse;
  disclaimer?: StepDisclaimer;
  otherButton?: NextStepActionResponse | null;
  otherText?: NextStepActionResponse | null;
  immediateSubmit?: boolean;
  showAsDropdown?: boolean;
  followUpInputs?: InputResponse[];
}

const BUILDER_SOURCE = "Builder";
const REFERRAL_SOURCES = new Set(["Referred by Loan Officer", "Referred by Realtor", "Friend or Family"]);
const REFERRAL_FIELD_NAMES = [
  "AttributionReferrerFirstName",
  "AttributionReferrerLastName",
  "AttributionReferrerEmail",
  "AttributionReferrerPhone",
];
const BUILDER_FIELD_NAMES = ["AttributionBuilderName", "AttributionBuilderEmail"];

interface FollowUpFieldDef {
  name: string;
  label: string;
  dataType: string;
  placeholder: string;
}

const FOLLOW_UP_INTRO: Record<Exclude<FollowUpKind, null>, string> = {
  builder: "Please provide builder contact information.",
  referral: "Enter name and contact information of referrer.",
};

const FOLLOW_UP_FIELDS: Record<Exclude<FollowUpKind, null>, FollowUpFieldDef[]> = {
  builder: [
    { name: "AttributionBuilderName", label: "Builder Name", dataType: "rawString", placeholder: "Enter builder name" },
    { name: "AttributionBuilderEmail", label: "Builder Contact Email", dataType: "emailAddress", placeholder: "Enter builder email" },
  ],
  referral: [
    { name: "AttributionReferrerFirstName", label: "First Name", dataType: "rawString", placeholder: "Enter first name" },
    { name: "AttributionReferrerLastName", label: "Last Name", dataType: "rawString", placeholder: "Enter last name" },
    { name: "AttributionReferrerEmail", label: "Email", dataType: "emailAddress", placeholder: "Enter email" },
    { name: "AttributionReferrerPhone", label: "Phone", dataType: "phone", placeholder: "Enter phone number" },
  ],
};

type FollowUpKind = "builder" | "referral" | null;

const followUpKindFor = (label?: string): FollowUpKind => {
  if (!label) return null;
  if (label === BUILDER_SOURCE) return "builder";
  if (REFERRAL_SOURCES.has(label)) return "referral";
  return null;
};

const fieldNamesFor = (kind: FollowUpKind) => {
  if (kind === "builder") return BUILDER_FIELD_NAMES;
  if (kind === "referral") return REFERRAL_FIELD_NAMES;
  return [];
};

const EMPTY_INPUT_ID = "00000000-0000-0000-0000-000000000000";
const ALL_FOLLOW_UP_FIELDS = [...FOLLOW_UP_FIELDS.referral, ...FOLLOW_UP_FIELDS.builder];

const requiredText = (label: string) => z.string().min(1, `${label} is required`);

const requiredEmail = (label: string) =>
  requiredText(label).email("Enter a valid email");

const requiredPhone = (label: string) =>
  requiredText(label).refine((value) => value.replace(/\D/g, "").length === 10, "Enter a valid phone number");

const schemaForField = (field: FollowUpFieldDef) => {
  if (field.dataType === "emailAddress") return requiredEmail(field.label);
  if (field.dataType === "phone") return requiredPhone(field.label);
  return requiredText(field.label);
};

const followUpSchemaFor = (kind: FollowUpKind, labels: Record<string, string> = {}) => {
  const shape: Record<string, z.ZodType<string>> = {};
  if (kind) {
    FOLLOW_UP_FIELDS[kind].forEach((field) => {
      shape[field.name] = schemaForField({ ...field, label: labels[field.name] || field.label });
    });
  }
  return z.object(shape);
};

const validateFollowUp = (kind: FollowUpKind, values: Record<string, string>, labels: Record<string, string> = {}) => {
  const payload = Object.fromEntries(
    (kind ? FOLLOW_UP_FIELDS[kind] : []).map((field) => [field.name, (values[field.name] ?? "").trim()])
  );
  const result = followUpSchemaFor(kind, labels).safeParse(payload);
  if (result.success) return {};

  return result.error.issues.reduce<Record<string, string>>((errors, issue) => {
    const key = issue.path[0];
    if (typeof key === "string" && !errors[key]) {
      errors[key] = issue.message;
    }
    return errors;
  }, {});
};

const QuestionnaireChoiceStep = ({
  stepActions,
  stepCode,
  stepLabel,
  metadata,
  skipAction,
  disclaimer,
  otherButton,
  otherText,
  immediateSubmit = false,
  showAsDropdown = false,
  followUpInputs = [],
}: QuestionnaireChoiceStepProps) => {
  const [selectedActionId, setSelectedActionId] = useState<string | undefined>(
    stepActions.find(({ hasPreviouslySelected }) => hasPreviouslySelected)?.stepActionId
  );
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    stepActions.find(({ hasPreviouslySelected }) => hasPreviouslySelected === true)?.label
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(followUpInputs.map((input) => [input.name, input.initialValue ?? ""]))
  );
  const [showOtherTextInput, setShowOtherTextInput] = useState(false);

  const followUpKind = followUpKindFor(selectedOption);
  const visibleFieldNames = fieldNamesFor(followUpKind);
  const showInlineFollowUp = stepCode === "B15" || followUpInputs.length > 0;
  const visibleFollowUpInputs = showInlineFollowUp && followUpKind
    ? FOLLOW_UP_FIELDS[followUpKind].map((field) => {
        const match = followUpInputs.find((input) => input.name === field.name || input.label === field.label);
        return {
          name: field.name,
          label: match?.label || field.label,
          placeholder: match?.placeholder || field.placeholder,
          dataType: match?.dataType || field.dataType,
          inputId: match?.inputId ?? "",
          initialValue: match?.initialValue ?? "",
        };
      })
    : [];
  const followUpLabels = Object.fromEntries(visibleFollowUpInputs.map((input) => [input.name, input.label]));
  const followUpErrors = validateFollowUp(followUpKind, fieldValues, followUpLabels);
  const followUpComplete = !followUpKind || Object.keys(followUpErrors).length === 0;

  const [updateApplication] = useSetApplicationStepAnswerMutation({
    fixedCacheKey: "updateApplication",
  });
  const [updateCyberSecurityAuditLog] = useUpdateStepAnswerCyberSecurityAuditLogMutation();
  const applicationId = useSelector(getCurrentUserApplicationId);

  const submitAnswer = async (stepActionId: string) => {
    await updateApplication({ id: applicationId, stepActionId });
    trackFSStepCompleted(stepCode, stepLabel, false);
    void updateCyberSecurityAuditLog(applicationId);
  };

  const handleButtonClick = (stepActionId: string) => {
    setSelectedActionId(stepActionId);
    if (immediateSubmit) {
      void submitAnswer(stepActionId);
    }
  };

  const handleContinue = async () => {
    if (!selectedActionId) return;
    await submitAnswer(selectedActionId);
  };

  const handleOptionChange = (option: string) => {
    setSelectedOption(option);
    setErrorMessage(undefined);
    setTouchedFields({});
    const kind = followUpKindFor(option);
    setFieldValues((current) => {
      const next = { ...current };
      if (kind !== "referral") {
        REFERRAL_FIELD_NAMES.forEach((name) => {
          next[name] = "";
        });
      }
      if (kind !== "builder") {
        BUILDER_FIELD_NAMES.forEach((name) => {
          next[name] = "";
        });
      }
      return next;
    });
  };

  const handleDropdownSubmit = async () => {
    if (!selectedOption) {
      setErrorMessage("Please select an option");
      return;
    }
    const selectedStepAction = stepActions.find(({ label }) => label === selectedOption);
    if (!selectedStepAction) return;

    if (followUpInputs.length === 0 && stepCode !== "B15") {
      await submitAnswer(selectedStepAction.stepActionId);
      return;
    }

    const errors = validateFollowUp(followUpKind, fieldValues, followUpLabels);
    if (Object.keys(errors).length > 0) {
      setTouchedFields((current) => ({
        ...current,
        ...Object.fromEntries(Object.keys(errors).map((name) => [name, true])),
      }));
      return;
    }

    const answeredInputs = followUpInputs.length > 0
      ? followUpInputs.map((input) => ({
          inputId: input.inputId,
          name: input.name,
          submittedValue: visibleFieldNames.includes(input.name) ? (fieldValues[input.name] ?? "").trim() : "",
        }))
      : ALL_FOLLOW_UP_FIELDS.map((field) => ({
          inputId: EMPTY_INPUT_ID,
          name: field.name,
          submittedValue: visibleFieldNames.includes(field.name) ? (fieldValues[field.name] ?? "").trim() : "",
        }));

    await updateApplication({
      id: applicationId,
      stepActionId: selectedStepAction.stepActionId,
      answeredInputs,
    });
    trackFSStepCompleted(stepCode, stepLabel, false);
    void updateCyberSecurityAuditLog(applicationId);
  };

  return (
    <>
      {metadata && metadata.length > 0 && <Metadata metadata={metadata} />}
      {showAsDropdown ? (
        <>
          <div className="select">
            <Select
              options={stepActions.map(({ label }) => label)}
              onChange={handleOptionChange}
              initialValue={selectedOption}
              errorMessage={errorMessage}
            />
          </div>
          {visibleFollowUpInputs.length > 0 && (
            <div className="single-choice-follow-up">
              {followUpKind && <p className="single-choice-follow-up__intro">{FOLLOW_UP_INTRO[followUpKind]}</p>}
              {visibleFollowUpInputs.map((input) =>
                input.dataType === "phone" ? (
                  <PhoneNumberInput
                    key={`${selectedOption ?? ""}-${input.name}`}
                    label={input.label}
                    placeholder={input.placeholder}
                    value={fieldValues[input.name] ?? ""}
                    errorMessage={touchedFields[input.name] ? followUpErrors[input.name] : undefined}
                    onChange={(value) => {
                      setFieldValues((current) => ({ ...current, [input.name]: value }));
                      setTouchedFields((current) => ({ ...current, [input.name]: true }));
                    }}
                  />
                ) : (
                  <Input
                    key={`${selectedOption ?? ""}-${input.name}`}
                    name={input.name}
                    label={input.label}
                    dataType={input.dataType}
                    placeholder={input.placeholder}
                    initialValue={fieldValues[input.name] ?? ""}
                    errorMessage={touchedFields[input.name] ? followUpErrors[input.name] : undefined}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setFieldValues((current) => ({ ...current, [input.name]: value }));
                      setTouchedFields((current) => ({ ...current, [input.name]: true }));
                    }}
                  />
                )
              )}
            </div>
          )}
          <QuestionActions skip={skipAction && <SkipButton skipAction={skipAction} />} />
          <QuestionNavigation>
            <Button
              title="Continue"
              className="question-navigation__continue"
              disabled={Boolean(selectedOption && followUpKind && !followUpComplete)}
              onClick={() => void handleDropdownSubmit()}
            />
          </QuestionNavigation>
        </>
      ) : (
        <>
          <div className="radio-choice-list">
            {stepActions.map(({ stepActionId, label, actionType }) => {
              const isSelected = selectedActionId === stepActionId;
              const isLoop = actionType === "loop";
              return (
                <button
                  key={stepActionId}
                  className={`radio-choice-item${isSelected ? " radio-choice-item--selected" : ""}${isLoop ? " radio-choice-item--loop" : ""}`}
                  onClick={() => handleButtonClick(stepActionId)}
                  type="button"
                >
                  <FontAwesomeIcon
                    icon={(isSelected ? faCircleDot : faCircle) as IconProp}
                    className="radio-choice-item__icon"
                  />
                  <span className="radio-choice-item__label">{label}</span>
                </button>
              );
            })}
          </div>
          {otherButton && otherText && (
            <OtherButtonAndText
              {...otherButton}
              showOtherTextInput={showOtherTextInput}
              setShowOtherTextInput={setShowOtherTextInput}
              otherText={otherText}
            />
          )}
          <QuestionActions skip={skipAction && <SkipButton skipAction={skipAction} />} />
          <QuestionNavigation>
            {!immediateSubmit && (
              <Button
                title="Continue"
                onClick={() => void handleContinue()}
                className="question-navigation__continue"
                disabled={!selectedActionId}
              />
            )}
          </QuestionNavigation>
        </>
      )}
      {disclaimer && <DisclaimerText Header={disclaimer.header} Text={disclaimer.text} />}
    </>
  );
};

export default QuestionnaireChoiceStep;
